import { Input } from '@frontend/shadcn/components/ui/input';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import { HandWaveIcon, PlusIcon, MinusIcon, TrashIcon, CircleInfoIcon } from '@revfluence/fresh-icons/solid/esm';
import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import { ImagePreview, RichTextEditor, Switcher } from '@frontend/app/refresh-components';
import { Button } from '@frontend/shadcn/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRecruitment, usePulseHeroPage } from '../RecruitmentContext';
import { getIconOptionByName, ImageIconSelector } from '../../../CampaignPage/components/ImageIconSelector';

export const PulseHeroPage = () => {
  const {
    addPulseHeroContentCarousel,
    removePulseHeroContentCarousel,
    setPulseHeroTitle,
    setPulseHeroDescription,
    setPulseHeroHasDescription,
    setPulseHeroListStyleType,
    setPulseHeroListItemText,
    removePulseHeroListItem,
    addPulseHeroListItem,
  } = useRecruitment();

  const heroPage = usePulseHeroPage();

  if (!heroPage) {
    return null;
  }

  const {
    carouselImages,
    title,
    description,
    hasDescription,
    listStyleType,
    listItems,
  } = heroPage;

  const handleAddPulseHeroContentCarousel = (content: string) => {
    addPulseHeroContentCarousel(content);
  };

  const handleRemovePulseHeroContentCarousel = (index: number) => {
    removePulseHeroContentCarousel(index);
  };

  const handleMediaSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          handleAddPulseHeroContentCarousel(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  return (
    <Accordion type="multiple" className="[&_h3]:mb-0" defaultValue={['hero']}>
      <AccordionItem value="hero" className="border-b-0">
        <AccordionTrigger>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-green-5 rounded-lg text-primary-foreground flex items-center justify-center">
              <HandWaveIcon fontSize={12} />
            </div>
            <span className="text-base font-medium">Hero</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="pb-0">
          {/* Content Carousel Section */}
          <div className="flex flex-col gap-2 items-start">
            <div className="flex items-center gap-1">
              <label className="text-xs font-medium">Content Carousel <span className="text-primaryMuted font-normal">(Required)</span></label>
              <CircleInfoIcon fontSize={12} className="text-grey-4" />
            </div>
            <div className="flex gap-3 flex-wrap">
              {carouselImages.map((content, index) => (
                <ImagePreview key={index} imageUrl={content} onRemove={() => handleRemovePulseHeroContentCarousel(index)} />
                    ))}
            </div>
            <Button
              variant="link"
              size="xs"
              className="gap-2 pl-0"
              onClick={handleMediaSelect}
            >
              <PlusIcon />
              Add Image or Video
            </Button>
          </div>

          <Separator className="my-4" />

          {/* Title Section */}
          <div className="flex flex-col gap-2">
            <label htmlFor="title" className="text-xs font-medium text-foreground">Title <span className="text-primaryMuted font-normal">(Required)</span></label>
            <div className="px-[1px]">
              <Input id="title" placeholder="Join Our Affiliate Program!" value={title} onChange={(e) => setPulseHeroTitle(e.target.value)} />
            </div>
          </div>

          {/* Description Section */}
          <div className="mt-2">
            {hasDescription && (
              <div className="flex flex-col items-start gap-2">
                <Button
                  variant="link"
                  className="gap-2 pl-0"
                  onClick={() => setPulseHeroHasDescription(false)}
                  size="xs"
                >
                  <MinusIcon />
                  Remove Description
                </Button>
                <RichTextEditor
                  placeholder="Welcome to your page! Write something amazing about your brand and give creators more information about this collaboration."
                  value={description}
                  onChange={setPulseHeroDescription}
                  className="[&_.ql-editor]:min-h-[100px]"
                />
              </div>
            )}
            {!hasDescription && (
              <Button
                variant="link"
                className="gap-2 pl-0"
                onClick={() => setPulseHeroHasDescription(true)}
                size="xs"
              >
                <PlusIcon />
                Add Description
              </Button>
            )}
          </div>

          <Separator className="my-4" />

          {/* List Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <label className="text-xs font-medium text-foreground">List</label>
                <CircleInfoIcon fontSize={12} className="text-grey-4" />
              </div>
              <Button variant="ghost" size="xs">
                <TrashIcon className="text-destructive" />
              </Button>
            </div>
            <Switcher
              items={[{
                key: 'number',
                label: 'Number',
              }, {
                key: 'bullet',
                label: 'Bullet',
              }, {
                key: 'icon',
                label: 'Icon',
              }, {
                key: 'none',
                label: 'None',
              }]}
              type="single"
              activeKeys={[listStyleType]}
              onChange={(value) => setPulseHeroListStyleType(value[0] as 'number' | 'bullet' | 'icon' | 'none')}
            />

            {/* List Items */}
            <div className="flex flex-col gap-4 mt-4">
              {
                listItems.map((item, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center justify-between gap-1">
                      <label className="text-xs font-medium">List Item</label>
                      <Button variant="ghost" size="xs" onClick={() => removePulseHeroListItem(index)}>
                        <TrashIcon className="text-destructive" />
                      </Button>
                    </div>
                    <div className="flex items-start space-x-2 px-[1px]">
                      {listStyleType === 'icon' && (
                        <ImageIconSelector
                          iconName="sack-dollar"
                          imageUrl=""
                          onImageChange={() => {}}
                          onIconChange={(icon) => setPulseHeroListItemText(index, item.text, icon)}
                          onImageUrlChange={() => {}}
                          onlyIcon
                          triggerBody={
                            <Button variant="outline" size="headerIcon">
                              <FontAwesomeIcon icon={getIconOptionByName(item.icon || 'sack-dollar')?.icon} className="h-3 w-3" />
                            </Button>
                          }
                        />
                      )}
                      <RichTextEditor
                        excludeModules={['link']}
                        value={item.text}
                        onChange={(value) => setPulseHeroListItemText(index, value, item.icon)}
                      />
                    </div>
                  </div>
                ))
              }
            </div>

            <Button
              variant="link"
              className="gap-2 pl-0 mt-4"
              onClick={() => addPulseHeroListItem({ text: '', icon: '' })}
              size="xs"
            >
              <PlusIcon />
              Add List Item
            </Button>
          </div>

          <Separator className="my-4" />

          {/* Primary Button Label Section */}
          <div>
            <label htmlFor="buttonLabel" className="text-xs font-medium text-foreground">Primary Button Label <span className="text-primaryMuted">(Required)</span></label>
            <div className="px-[1px] pb-[1px]">
              <Input id="buttonLabel" placeholder="Get Started" className="mt-2" />
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
